import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type {
  PayrollRoster,
  SavePayrollRosterDraftPayload,
} from "@/payroll/roster/types";
import {
  buildDiscardedRosterCacheEntry,
  buildPublishedRosterCacheEntry,
  buildWorkingRosterCacheEntry,
} from "./cache-entries";
import { payrollRosterKey } from "./keys";
import { payrollRosterDraftPath, payrollRosterPublishPath } from "./paths";
import { useRosterViewer } from "./use-roster-viewer";

export const useSavePayrollRosterDraft = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { viewer } = useRosterViewer(businessId);

  return useMutation({
    mutationFn: async (payload: SavePayrollRosterDraftPayload) => {
      if (!businessId) throw new Error("Business is required");

      return api
        .put<PayrollRoster>(payrollRosterDraftPath(businessId), payload)
        .then((response) => response.data);
    },
    onSuccess: (savedRoster) => {
      queryClient.setQueryData<PayrollRoster | null>(
        payrollRosterKey(businessId, viewer),
        (current) => buildWorkingRosterCacheEntry(savedRoster, current),
      );
    },
  });
};

export const usePublishPayrollRoster = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { viewer } = useRosterViewer(businessId);

  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error("Business is required");

      return api
        .post<PayrollRoster>(payrollRosterPublishPath(businessId))
        .then((response) => response.data);
    },
    onSuccess: (publishedRoster) => {
      queryClient.setQueryData<PayrollRoster | null>(
        payrollRosterKey(businessId, viewer),
        (current) => buildPublishedRosterCacheEntry(publishedRoster, current),
      );
      void queryClient.invalidateQueries({
        queryKey: payrollRosterKey(businessId),
      });
    },
  });
};

export const useDiscardPayrollRosterDraft = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { viewer } = useRosterViewer(businessId);

  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error("Business is required");

      await api.delete(payrollRosterDraftPath(businessId));
    },
    onSuccess: () => {
      queryClient.setQueryData<PayrollRoster | null>(
        payrollRosterKey(businessId, viewer),
        buildDiscardedRosterCacheEntry,
      );

      return queryClient.invalidateQueries({
        queryKey: payrollRosterKey(businessId),
      });
    },
  });
};
